import { useState, useEffect, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { MapPin, Route, DollarSign, Loader2, AlertTriangle, Signpost } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import StopsMap from './corridors/StopsMap';
import CorridorBuilder from './corridors/CorridorBuilder';
import FareMatrix from './corridors/FareMatrix';
import FareGaps from './corridors/FareGaps';
import HubStands from './corridors/HubStands';

type SubTab = 'stops' | 'builder' | 'fares' | 'gaps' | 'stands';

// Types matching admin_list_corridor_data response
export interface TransitStop {
  id: string;
  name: string;
  display_name: string | null;
  lat: number;
  lng: number;
  aliases: string[] | null;
  notes: string | null;
  is_active: boolean;
  is_draft: boolean;
  is_hub?: boolean;
  hub_radius_m?: number | null;
  updated_at: string | null;
  updated_by: string | null;
}

export interface CorridorData {
  id: string;
  name: string;
  description: string | null;
  direction: string;
  is_active: boolean;
  is_draft: boolean;
  published_at: string | null;
  updated_at: string | null;
  stops: CorridorStopEntry[];
}

export interface CorridorStopEntry {
  stop_id: string;
  stop_name: string;
  display_name: string | null;
  lat: number | null;
  lng: number | null;
  position: number;
  is_off_route: boolean;
  is_draft: boolean;
}

export interface FareEntry {
  id: string;
  origin: string;
  destination: string;
  fare_xcd: number;
  notes: string | null;
  is_active: boolean;
  is_draft: boolean;
  updated_at: string | null;
}

export default function Corridors() {
  const [activeTab, setActiveTab] = useState<SubTab>('stops');
  const [stops, setStops] = useState<TransitStop[]>([]);
  const [corridors, setCorridors] = useState<CorridorData[]>([]);
  const [fares, setFares] = useState<FareEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Single RPC call for all corridor data
  const fetchData = useCallback(async () => {
    try {
      const { data, error: rpcError } = await supabase.rpc('admin_list_corridor_data');
      if (rpcError) {
        const msg = rpcError.message || JSON.stringify(rpcError);
        console.error('[corridor-load]', rpcError);
        setError(msg);
        toast.error(`Load failed: ${msg}`);
        return;
      }

      setError(null);

      // The RPC returns { stops: [...], corridors: [...], fares: [...] }
      const result = data as any;
      if (result) {
        setStops(Array.isArray(result.stops) ? result.stops : []);
        setCorridors(Array.isArray(result.corridors) ? result.corridors : []);
        setFares(Array.isArray(result.fares) ? result.fares : []);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : JSON.stringify(err);
      console.error('[corridor-load]', err);
      setError(msg);
      toast.error(`Load failed: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Realtime subscription — listen for changes and refetch
  useEffect(() => {
    const channel = supabase
      .channel('admin-corridors')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'transit_stops' }, () => {
        fetchData();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'corridors' }, () => {
        fetchData();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'corridor_stop_membership' }, () => {
        fetchData();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'corridor_fares' }, () => {
        fetchData();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [fetchData]);

  const draftStops = stops.filter(s => s.is_draft).length;
  const draftCorridors = corridors.filter(c => c.is_draft).length;
  const draftFares = fares.filter(f => f.is_draft).length;

  const tabs: { id: SubTab; label: string; icon: typeof MapPin; badge?: number }[] = [
    { id: 'stops', label: 'Stops Map', icon: MapPin, badge: draftStops || undefined },
    { id: 'builder', label: 'Corridor Builder', icon: Route, badge: draftCorridors || undefined },
    { id: 'fares', label: 'Fare Matrix', icon: DollarSign, badge: draftFares || undefined },
    { id: 'gaps', label: 'Fare Gaps', icon: AlertTriangle },
    { id: 'stands', label: 'Hub Stands', icon: Signpost },
  ];

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <div className="max-w-lg text-center space-y-4">
          <h2 className="text-xl font-semibold text-white">Failed to load corridor data</h2>
          <p className="text-gray-400 text-sm leading-relaxed font-mono bg-gray-800/50 rounded-lg p-4">
            {error}
          </p>
          <button
            onClick={() => { setLoading(true); setError(null); fetchData(); }}
            className="px-4 py-2 bg-emerald-600 text-white rounded-md text-sm hover:bg-emerald-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col">
      {/* Sub-tab navigation */}
      <div className="px-4 py-2 border-b border-gray-800 flex items-center gap-1 bg-gray-900/50">
        {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                isActive
                  ? 'bg-emerald-600/20 text-emerald-400 border border-emerald-600/30'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
              {tab.badge && tab.badge > 0 && (
                <Badge className="bg-blue-600/20 text-blue-400 border-blue-600/30 text-[10px] px-1.5 py-0 ml-1">
                  {tab.badge}
                </Badge>
              )}
            </button>
          );
        })}

        <div className="flex-1" />

        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span>{stops.length} stops</span>
          <span>{corridors.length} corridors</span>
          <span>{fares.length} fares</span>
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'stops' && (
          <StopsMap
            stops={stops}
            corridors={corridors}
            onRefresh={fetchData}
            onNavigateToCorridor={(id) => {
              setActiveTab('builder');
            }}
          />
        )}
        {activeTab === 'builder' && (
          <CorridorBuilder
            corridors={corridors}
            stops={stops}
            onRefresh={fetchData}
            onSwitchToStops={(focusStopId) => {
              setActiveTab('stops');
              // TODO: pass focusStopId to StopsMap if needed
            }}
          />
        )}
        {activeTab === 'fares' && (
          <FareMatrix
            fares={fares}
            stops={stops}
            corridors={corridors}
            onRefresh={fetchData}
          />
        )}
        {activeTab === 'gaps' && <FareGaps onRefresh={fetchData} />}
        {activeTab === 'stands' && <HubStands stops={stops} corridors={corridors} />}
      </div>
    </div>
  );
}
